import React, { FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, CardMedia, Fade, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { TransitionGroup } from 'react-transition-group';
import wellnessScan from '@assets/landing/wellness-scan.jpg';
import scanOutcomes from '@assets/landing/scan-outcomes.jpg';
import tryOn from '@assets/landing/fit-tryon.jpg';
import { selectCurrentRegion, selectIsAuthenticated, selectIsAuthLoading } from '../../auth/authSlice';
import { useAppSelector } from '../../../../app/hooks';

import { MeasurementIcon, ScannersIcon, ScansIcon } from '../IconSelectorComponent/IconSelectorComponent';
import LoginButton from '../../auth/LoginButton/LoginButton';
import LoadingComponent from '../LoadingComponent/LoadingComponent';
import ModalDialogComponent from '../ModalDialogComponent/ModalDialogComponent';
import boddVariables from '../../../../variables.module.scss';

interface LandingComponentProps {
}

const LandingComponent: FC<LandingComponentProps> = () => {
    const theme = useTheme();

    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const isAuthLoading = useAppSelector(selectIsAuthLoading);
    const currentRegionPath = useAppSelector(selectCurrentRegion).toLowerCase();
    const ref = useRef<HTMLObjectElement>(null);
    const [joinModalOpen, setJoinModalOpen] = useState(false);
    const [step, setStep] = useState<number>(0);
    const [zoomDirection, setZoomDirection] = useState({ x: 0.5, y: -0.5 });
    const isMobileLayout = useMediaQuery(theme.breakpoints.down('md'));
    const caurasalStepOpacity = isMobileLayout ? 0.3 : 0.2;
    const caurasalTransitionPeriod = 800;
    const caurasalStepPeriod = 10000;
    const navigate = useNavigate();

    useEffect(() => {
        const interval = setInterval(() => {
            setStep((step + 1) % 3);
            setZoomDirection({
                x: (Math.random() - 0.5) * 0.8,
                y: (Math.random() - 0.5) * 0.8,
            });
        }, caurasalStepPeriod);
        return () => clearInterval(interval);
    }, [step]);

    const click = () => window.open('https://bodd.io', '_blank');

    const attachClick = (e: any) => {
        e.target.contentDocument.addEventListener('click', click);
        e.target.contentDocument.children[0].style.cursor = 'pointer';
    };

    useEffect(() => {
        const obj = ref.current;
        if (obj) {
            obj.addEventListener('load', attachClick);
        }

        return () => {
            if (obj) {
                obj.removeEventListener('load', attachClick);
            }
        };
    }, [isAuthLoading]);

    useEffect(() => {
        if (isAuthenticated) {
            navigate(`/${currentRegionPath}/scans`);
        }
    }, [isAuthenticated]);

    if (isAuthLoading) {
        return <LoadingComponent isLoading />;
    }

    const handleCarouselStepClick = (stepIndex: number) => {
        setStep(stepIndex);
    };

    const carousel = [
        {
            Icon: <ScansIcon
                fontSize={isMobileLayout ? '40px' : '48px'}
                color={isMobileLayout ? boddVariables.white : boddVariables.boddBlack}
            />,
            text: 'Configure scanner set up & account settings',
            image: wellnessScan,
            imageLabel: 'Person getting a scan',
            backgroundPosition: 'right top',
        },
        {
            Icon: <ScannersIcon
                fontSize={isMobileLayout ? '40px' : '48px'}
                color={isMobileLayout ? boddVariables.white : boddVariables.boddBlack}
            />,
            text: 'Manage scanners & profiles',
            image: tryOn,
            imageLabel: 'Person trying on recommended clothes',
            backgroundPosition: 'right top',
        },
        {
            Icon: <MeasurementIcon
                fontSize={isMobileLayout ? '40px' : '48px'}
                color={isMobileLayout ? boddVariables.white : boddVariables.boddBlack}
            />,
            text: 'View insights & recommendations',
            image: scanOutcomes,
            imageLabel: 'Visual representation of scan outcomes',
            backgroundPosition: 'center center',
        },
    ];

    return (
        <Box
            data-testid="LandingComponent"
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                flexDirection: isMobileLayout ? 'column-reverse' : 'row',
                flex: 1,
                height: '100%',
                overflow: 'hidden',
                background: isMobileLayout ? 'linear-gradient(0deg, #000 50%, rgba(0, 0, 0, 0.00) 95%)' : boddVariables.grey200,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMobileLayout ? 'column-reverse' : 'column',
                    p: isMobileLayout ? 0 : 5,
                    borderRadius: isMobileLayout ? '0px' : '0px 24px 24px 0px',
                    width: isMobileLayout ? '100%' : 'auto',
                    bgcolor: isMobileLayout ? boddVariables.boddBlack : boddVariables.white,
                    zIndex: 200,
                    maxWidth: isMobileLayout ? '100%' : 400,
                }}
            >
                <Stack
                    direction="column"
                    justifyContent="flex-start"
                    alignItems={isMobileLayout ? 'center' : 'flex-start'}
                    sx={{
                        borderRadius: isMobileLayout ? '20px 20px 0px 0px ' : '0px',
                        bgcolor: boddVariables.white,
                        p: isMobileLayout ? 3 : 0,
                        pt: isMobileLayout ? 3.5 : 0,
                    }}
                >
                    <object
                        ref={ref}
                        height="70px"
                        width="70px"
                        type="image/svg+xml"
                        data="/bodd-logo.svg"
                        data-testid="BoddIconComponent"
                        aria-label="Bodd"
                    />
                    <Typography
                        variant="h1"
                        sx={{
                            mt: 2.75,
                            letterSpacing: 0.15,
                            fontSize: isMobileLayout ? '2.375em' : '3.375em',
                        }}
                    >
                        BoddHub
                    </Typography>
                    <div>
                        <Button
                            variant="outlined"
                            sx={{ mr: '7px' }}
                            onClick={() => setJoinModalOpen(true)}
                        >
                            Join
                        </Button>
                        <LoginButton />
                    </div>
                </Stack>
                <div
                    style={{ flexGrow: 1 }}
                />
                <Box
                    sx={{
                        p: isMobileLayout ? 3 : 0,
                        pt: 0,
                        mt: isMobileLayout ? -4 : 0,
                    }}
                >
                    <TransitionGroup>
                        <Fade
                            key={step}
                            exit={false}
                            timeout={{ appear: caurasalTransitionPeriod, enter: caurasalTransitionPeriod }}
                        >
                            <Stack
                                sx={{
                                    alignItems: isMobileLayout ? 'center' : 'flex-start',
                                }}
                            >
                                {carousel[step].Icon}
                                <Typography
                                    variant="h2"
                                    sx={{
                                        mt: '12px',
                                        mb: 4,
                                        mx: isMobileLayout ? 2 : 0,
                                        color: isMobileLayout ? boddVariables.white : boddVariables.boddBlack,
                                        textAlign: isMobileLayout ? 'center' : 'left',
                                        fontSize: isMobileLayout ? '1.25em' : '1.6875em',
                                    }}
                                >
                                    {carousel[step].text}
                                </Typography>
                            </Stack>
                        </Fade>
                    </TransitionGroup>
                    <div
                        style={{ display: 'flex', flexDirection: 'row', gap: '5px' }}
                    >
                        <Button
                            sx={{
                                flexGrow: 1,
                                p: 0,
                                height: '3px',
                                bgcolor: isMobileLayout ? boddVariables.white : boddVariables.boddBlack,
                                opacity: step === 0 ? '1' : caurasalStepOpacity,
                                '&:hover': { opacity: 1, backgroundColor: boddVariables.brandBlue },
                            }}
                            onClick={() => handleCarouselStepClick(0)}
                            disableElevation
                        />
                        <Button
                            sx={{
                                flexGrow: 1,
                                p: 0,
                                height: '3px',
                                bgcolor: isMobileLayout ? boddVariables.white : boddVariables.boddBlack,
                                opacity: step === 1 ? '1' : caurasalStepOpacity,
                                '&:hover': { opacity: 1, backgroundColor: boddVariables.brandBlue },
                            }}
                            onClick={() => handleCarouselStepClick(1)}
                            disableElevation
                        />
                        <Button
                            sx={{
                                flexGrow: 1,
                                p: 0,
                                height: '3px',
                                bgcolor: isMobileLayout ? boddVariables.white : boddVariables.boddBlack,
                                opacity: step === 2 ? '1' : caurasalStepOpacity,
                                '&:hover': { opacity: 1, backgroundColor: boddVariables.brandBlue },
                            }}
                            onClick={() => handleCarouselStepClick(2)}
                            disableElevation
                        />
                    </div>
                </Box>
            </Box>

            <TransitionGroup component={null}>
                <Fade
                    key={step}
                    exit={false}
                    timeout={{ appear: caurasalTransitionPeriod, enter: caurasalTransitionPeriod }}
                >
                    <CardMedia
                        image={carousel[step].image}
                        title={carousel[step].imageLabel}
                        sx={{
                            flexGrow: 1,
                            backgroundSize: 'cover',
                            backgroundPosition: carousel[step].backgroundPosition,
                            ml: isMobileLayout ? 0 : '-24px',
                            zIndex: 100,
                            '@keyframes diagonalZoomOut': {
                                '0%': {
                                    transform: `scale(1) translate(${zoomDirection.x}%, ${zoomDirection.y}%)`,
                                },
                                '100%': {
                                    transform: 'scale(1.025) translate(0, 0)',
                                },
                            },
                            animation: 'diagonalZoomOut 12s ease-out forwards',
                            '&::before': isMobileLayout ? {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(0deg, #000 0%, rgba(0, 0, 0, 0.00) 42.51%)',
                            } : {},
                            position: 'relative',
                        }}
                    />
                </Fade>
            </TransitionGroup>
            <ModalDialogComponent
                setOpen={setJoinModalOpen}
                title="How to join BoddHub"
                open={joinModalOpen}
                onConfirm={() => setJoinModalOpen(false)}
                confirmText="Ok got it"
            >
                To join, you need to be invited to your company’s Bodd account. Contact your company administrator to
                send you an invite.
            </ModalDialogComponent>
        </Box>
    );
};

export default LandingComponent;
